import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  ExternalLink,
  Star,
  Archive,
  Trash2,
  Clock,
  Circle,
  CheckCircle,
  Quote,
  FileText,
  MoreHorizontal,
} from 'lucide-react';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { trackEvent, AnalyticsEvent } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActionButton, StatusBadge, ReadingProgress } from '@/components/links';
import { useLink } from '@/hooks/useLink';
import { useUpdateLink } from '@/hooks/useUpdateLink';
import { useDeleteLink } from '@/hooks/useDeleteLink';
import { useReadingProgress } from '@/hooks/useReadingProgress';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

const proseClasses =
  'prose dark:prose-invert max-w-none font-serif text-[17px] [&>*:first-child]:mt-0 prose-headings:font-semibold prose-headings:leading-tight prose-h2:mt-8 prose-h2:mb-3 prose-h3:mt-6 prose-h3:mb-2 prose-h4:mt-5 prose-h4:mb-2 prose-p:my-4 prose-p:text-foreground prose-p:leading-[1.75] prose-li:my-2 prose-li:text-foreground prose-li:leading-[1.7] prose-blockquote:my-4 prose-blockquote:text-foreground/90 prose-blockquote:leading-[1.7]';

export function LinkDetail() {
  const { id } = useParams<{ id: string }>();
  const { getTracker, clearTracker } = useAnalytics();
  const hasTracked = useRef(false);
  const { progress } = useReadingProgress();
  const isMobile = useIsMobile();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: link, isLoading, isError, error } = useLink(id!);

  // Track load completion when data arrives or errors
  useEffect(() => {
    if (hasTracked.current || isLoading) return;

    const linkId = parseInt(id!, 10);
    const tracker = getTracker(linkId);

    if (isError || !link) {
      // Track failure
      if (tracker) {
        if (error?.message?.includes('not found') || error?.message?.includes('404')) {
          tracker.onNotFound();
        } else {
          tracker.onDetailLoaded(false, error?.message || 'Unknown error');
        }
        clearTracker(linkId);
      }
      hasTracked.current = true;
    } else if (link) {
      // Track success
      if (tracker) {
        tracker.onDetailLoaded(true);
        clearTracker(linkId);
      }
      hasTracked.current = true;
    }
  }, [id, link, isLoading, isError, error, getTracker, clearTracker]);

  const updateLink = useUpdateLink({
    onError: () => toast.error('Failed to update link'),
  });

  const deleteLink = useDeleteLink({
    redirectTo: '/dashboard',
    onSuccess: () => toast.success('Link deleted'),
    onError: () => toast.error('Failed to delete link'),
  });

  const handleToggleFavorite = () => {
    if (!link) return;
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'toggle_favorite',
      linkId: link.id,
      newValue: !link.is_favorite,
    });
    updateLink.mutate({
      id: link.id,
      updates: { is_favorite: !link.is_favorite },
    });
    toast.success(link.is_favorite ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleToggleArchive = () => {
    if (!link) return;
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'toggle_archive',
      linkId: link.id,
      newValue: !link.is_archived,
    });
    updateLink.mutate({
      id: link.id,
      updates: { is_archived: !link.is_archived },
    });
    toast.success(link.is_archived ? 'Unarchived' : 'Archived');
  };

  const handleMarkAsRead = () => {
    if (!link) return;
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'toggle_read',
      linkId: link.id,
      newValue: !link.is_read,
    });
    updateLink.mutate({
      id: link.id,
      updates: { is_read: !link.is_read },
    });
    toast.success(link.is_read ? 'Marked as unread' : 'Marked as read');
  };

  const handleDelete = () => {
    if (!link) return;
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'delete',
      linkId: link.id,
    });
    deleteLink.mutate(link.id);
  };

  // Format date for display
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl p-6">
        <div className="mb-6">
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !link) {
    return (
      <div className="container max-w-4xl p-6">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <h3 className="text-lg font-medium">Link not found</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {error?.message || "The link you're looking for doesn't exist."}
          </p>
          <Button variant="outline" className="mt-4" asChild>
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isProcessed = link.status === 'processed';

  return (
    <>
      {/* Reading Progress Indicator */}
      <ReadingProgress progress={progress} />

      <div className="container max-w-4xl p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start gap-4">
            {/* Favicon - only show if available */}
            {link.favicon_url && (
              <img
                src={link.favicon_url}
                alt=""
                className="h-8 w-8 rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}

            <div className="flex-1">
              <h1 className="font-serif text-2xl font-semibold leading-tight md:text-[28px]">
                {link.title || link.url}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span>{link.source_domain}</span>
                {link.reading_time && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{link.reading_time} min read</span>
                  </>
                )}
                {link.word_count && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{link.word_count.toLocaleString()} words</span>
                  </>
                )}
              </div>
            </div>

            {/* Status Badge */}
            {!isProcessed && <StatusBadge status={link.status} />}
          </div>

          {/* Tags */}
          {link.tags && link.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {link.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content Tabs with Actions */}
        {isProcessed && (
          <Tabs defaultValue="summary" className="w-full">
            <div className="flex items-center border-b">
              <TabsList className="h-auto flex-1 justify-start gap-6 rounded-none border-b-0 bg-transparent p-0">
                <TabsTrigger
                  value="summary"
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  <Quote className="mr-2 h-4 w-4" />
                  Summary
                </TabsTrigger>
                <TabsTrigger
                  value="reader"
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Reader
                </TabsTrigger>
              </TabsList>

              {/* Toolbar actions */}
              <div className="flex items-center gap-1">
                {/* Desktop: show individual action buttons */}
                {!isMobile && (
                  <>
                    <ActionButton aria-label="Open Original" asChild>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink />
                      </a>
                    </ActionButton>
                    <ActionButton
                      aria-label={link.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                      onClick={handleToggleFavorite}
                    >
                      <Star
                        className={cn(link.is_favorite && 'fill-yellow-400 !text-yellow-400')}
                      />
                    </ActionButton>
                    <ActionButton
                      aria-label={link.is_read ? 'Mark as unread' : 'Mark as read'}
                      onClick={handleMarkAsRead}
                    >
                      {link.is_read ? <CheckCircle /> : <Circle />}
                    </ActionButton>
                  </>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ActionButton aria-label="More actions">
                      <MoreHorizontal />
                    </ActionButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* Mobile: show these actions in the menu */}
                    {isMobile && (
                      <>
                        <DropdownMenuItem onClick={handleToggleFavorite}>
                          <Star
                            className={cn(
                              'mr-2 h-4 w-4',
                              link.is_favorite && 'fill-yellow-400 text-yellow-400'
                            )}
                          />
                          {link.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open original
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleMarkAsRead}>
                          {link.is_read ? (
                            <CheckCircle className="mr-2 h-4 w-4" />
                          ) : (
                            <Circle className="mr-2 h-4 w-4" />
                          )}
                          {link.is_read ? 'Mark as unread' : 'Mark as read'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={handleToggleArchive}>
                      <Archive className={cn('mr-2 h-4 w-4', link.is_archived && 'text-primary')} />
                      {link.is_archived ? 'Unarchive' : 'Archive'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Summary Tab */}
            <TabsContent value="summary" className="pt-6 space-y-8">
              <article className={proseClasses}>
                {/* Summary */}
                {link.summary && <p>{link.summary}</p>}

                {/* Key Quotes */}
                {link.key_quotes && link.key_quotes.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Key Quotes
                    </h3>
                    {link.key_quotes.map((quote, index) => (
                      <blockquote
                        key={index}
                        className="border-l-4 border-primary/30 pl-4 font-serif text-[17px] italic leading-[1.7] text-foreground/90"
                      >
                        <p>{typeof quote === 'string' ? quote : quote.text}</p>
                        {typeof quote !== 'string' && quote.context && (
                          <footer className="mt-2 font-sans text-xs not-italic text-muted-foreground">
                            — {quote.context}
                          </footer>
                        )}
                      </blockquote>
                    ))}
                  </div>
                )}
              </article>
            </TabsContent>

            {/* Reader Tab */}
            <TabsContent value="reader" className="pt-6">
              {link.content ? (
                <article className={proseClasses}>
                  <ReactMarkdown>{link.content}</ReactMarkdown>
                </article>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-4 font-medium">No content available</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    The full article content was not extracted.
                  </p>
                  <Button variant="outline" className="mt-4" asChild>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Read Original
                    </a>
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Processing/Failed States */}
        {!isProcessed && (
          <Card>
            <CardContent className="py-8">
              <div className="flex flex-col items-center justify-center text-center">
                {link.status === 'pending' && (
                  <>
                    <Clock className="h-12 w-12 text-muted-foreground/50" />
                    <h3 className="mt-4 font-medium">Waiting to process</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      This link is queued for processing. Check back soon.
                    </p>
                  </>
                )}
                {link.status === 'processing' && (
                  <>
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    <h3 className="mt-4 font-medium">Processing</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      We're extracting content and generating a summary...
                    </p>
                  </>
                )}
                {link.status === 'failed' && (
                  <>
                    <div className="rounded-full bg-destructive/10 p-3">
                      <Trash2 className="h-6 w-6 text-destructive" />
                    </div>
                    <h3 className="mt-4 font-medium">Processing failed</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {link.error_message || 'An error occurred while processing this link.'}
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata - only show if meaningful data exists beyond saved date */}
        {(link.processed_at || link.read_at || link.content_type) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                {link.processed_at && (
                  <div>
                    <dt className="text-muted-foreground">Processed</dt>
                    <dd>{formatDate(link.processed_at)}</dd>
                  </div>
                )}
                {link.read_at && (
                  <div>
                    <dt className="text-muted-foreground">Read</dt>
                    <dd>{formatDate(link.read_at)}</dd>
                  </div>
                )}
                {link.content_type && (
                  <div>
                    <dt className="text-muted-foreground">Type</dt>
                    <dd className="capitalize">{link.content_type}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        )}

        {/* Delete confirmation dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this link?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the link and all
                associated data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}
